import { Copy, ErrorOutline } from '@tunasong/icons'
import { errorReporterRelease, logger } from '@tunasong/models'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import styles from './block-error-boundary.module.css'

/** What Plate passes a node component: the element, its Slate attributes and its Slate children */
export interface NodeProps {
  attributes?: Record<string, unknown>
  element?: { id?: string; type?: string; name?: unknown }
  children?: ReactNode
}

export interface BlockErrorBoundaryOptions {
  /** The block's type as people read it, e.g. "Melody" */
  label: string
  /** An inline element (a mention, a link) gets an inline fallback, so the line around it keeps flowing */
  inline?: boolean
}

export interface BlockErrorCardProps extends BlockErrorBoundaryOptions {
  error: Error
  nodeProps: NodeProps
  onRetry(): void
}

const blockName = (element: NodeProps['element']) =>
  typeof element?.name === 'string' && element.name.trim() ? element.name : undefined

/** What Copy error details copies: enough to find the event, and no document content */
const errorDetails = (error: Error, element: NodeProps['element']) =>
  [
    `Error: ${error.message}`,
    `Block type: ${element?.type ?? 'unknown'}`,
    `Element id: ${element?.id ?? 'unknown'}`,
    `URL: ${window.location.href}`,
    `Release: ${errorReporterRelease() ?? 'unknown'}`,
  ].join('\n')

/**
 * The card in place of a block that failed to render (GH #2809, approved copy). The Slate children stay mounted under
 * it, with the element's attributes, so paths and the selection around the block still work.
 */
export const BlockErrorCard: FC<BlockErrorCardProps> = props => {
  const { error, nodeProps, label, inline = false, onRetry } = props
  const { attributes, element, children } = nodeProps
  const name = blockName(element)
  const title = name ? `${label} "${name}" couldn't be shown` : "This block couldn't be shown"

  const handleCopy = () => {
    navigator.clipboard.writeText(errorDetails(error, element)).catch((e: unknown) => logger.warn('Copy failed', e))
  }

  if (inline) {
    return (
      <span {...attributes}>
        <span className={styles.inline} contentEditable={false} data-block-error="" role="alert">
          <ErrorOutline aria-hidden="true" fontSize="small" />
          {title}
          <Button size="small" onClick={onRetry}>
            Try again
          </Button>
        </span>
        {children}
      </span>
    )
  }

  return (
    <div {...attributes}>
      <div className={styles.card} contentEditable={false} data-block-error="" role="alert">
        <ErrorOutline className={styles.icon} aria-hidden="true" />
        <div className={styles.text}>
          <div className={styles.title}>{title}</div>
          <div className={styles.subline}>The rest of the song still works. We&apos;ve been notified.</div>
        </div>
        <div className={styles.actions}>
          <Button size="small" variant="outlined" onClick={onRetry}>
            Try again
          </Button>
          <IconButton size="small" aria-label="Copy error details" onClick={handleCopy}>
            <Copy fontSize="small" />
          </IconButton>
        </div>
      </div>
      {children}
    </div>
  )
}
